import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { FileText, Download } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useSupportTicket, useAddTicketMessage } from "@/hooks/use-support-tickets";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { ImageViewerModal } from "./ImageViewerModal";

interface CompanyTicketDetailModalProps {
  ticketId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const CompanyTicketDetailModal = ({
  ticketId,
  open,
  onOpenChange,
}: CompanyTicketDetailModalProps) => {
  const [newMessage, setNewMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [imageViewerOpen, setImageViewerOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [imageAttachments, setImageAttachments] = useState<Array<{id: string, name: string, signedUrl: string}>>([]);
  
  const { data: ticket, isLoading } = useSupportTicket(ticketId);
  const addMessageMutation = useAddTicketMessage();
  const { toast } = useToast();

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !ticket) return;

    setIsSubmitting(true);
    try {
      await addMessageMutation.mutateAsync({
        ticket_id: ticket.id,
        message: newMessage.trim(),
        is_internal: false,
      });
      
      setNewMessage("");
      toast({
        title: "Mensagem enviada",
        description: "Sua mensagem foi adicionada ao chamado.",
      });
    } catch (error) {
      toast({
        title: "Erro",
        description: "Não foi possível enviar a mensagem.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getPriorityLabel = (priority: string) => {
    const labels = {
      low: 'Baixa',
      medium: 'Média', 
      high: 'Alta',
      urgent: 'Urgente'
    };
    return labels[priority as keyof typeof labels] || priority;
  };

  const getPriorityVariant = (priority: string) => {
    const variants = {
      low: 'secondary',
      medium: 'default',
      high: 'destructive', 
      urgent: 'destructive'
    };
    return variants[priority as keyof typeof variants] || 'default';
  };

  const getStatusLabel = (status: string) => {
    const labels = {
      open: 'Aberto',
      in_progress: 'Em Andamento',
      resolved: 'Resolvido',
      closed: 'Fechado'
    };
    return labels[status as keyof typeof labels] || status;
  };

  const downloadAttachment = async (attachment: any) => {
    try {
      const { data, error } = await supabase.storage
        .from('support-attachments')
        .download(attachment.path);

      if (error) throw error;

      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = attachment.name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast({
        title: "Sucesso",
        description: "Anexo baixado com sucesso",
      });
    } catch (error) {
      console.error('Error downloading attachment:', error);
      toast({
        title: "Erro",
        description: "Não foi possível baixar o arquivo.",
        variant: "destructive",
      });
    }
  };

  // Componente para preview de anexos
  const AttachmentPreview = ({ attachment, onDownload }: { attachment: any; onDownload: (attachment: any) => void }) => {
    const [imageUrl, setImageUrl] = useState<string | null>(null);
    const [isLoadingUrl, setIsLoadingUrl] = useState(false);
    const isImage = attachment.type?.startsWith('image/');
    
    useEffect(() => {
      if (isImage) {
        const getSignedUrl = async () => {
          setIsLoadingUrl(true);
          try {
            const { data, error } = await supabase.storage
              .from('support-attachments')
              .createSignedUrl(attachment.path, 3600);
            
            if (error) {
              console.error('Error creating signed URL:', error);
            } else if (data?.signedUrl) {
              setImageUrl(data.signedUrl);
            }
          } catch (error) {
            console.error('Error creating signed URL:', error);
          } finally {
            setIsLoadingUrl(false);
          }
        };
        
        getSignedUrl();
      }
    }, [attachment.path, isImage]);

    const handleImageClick = async () => {
      if (!isImage || !imageUrl) return;

      // Get all image attachments from the ticket
      const allAttachments = ticket?.attachments || [];
      const imageAttachments = allAttachments.filter((att: any) => att.type?.startsWith('image/'));
      
      // Create signed URLs for all images
      const imagesWithUrls = await Promise.all(
        imageAttachments.map(async (att: any) => {
          const { data } = await supabase.storage
            .from('support-attachments')
            .createSignedUrl(att.path, 3600);
          
          return {
            id: att.id,
            name: att.name,
            signedUrl: data?.signedUrl || ''
          };
        })
      );

      const currentIndex = imagesWithUrls.findIndex(img => img.id === attachment.id);
      
      setImageAttachments(imagesWithUrls.filter(img => img.signedUrl));
      setSelectedImageIndex(Math.max(0, currentIndex));
      setImageViewerOpen(true);
    };
    
    if (isImage) {
      if (isLoadingUrl) {
        return (
          <div className="flex items-center gap-3 p-2 border rounded-lg">
            <div className="w-20 h-20 bg-muted animate-pulse rounded border"></div>
            <div className="flex flex-col gap-1">
              <span className="text-sm font-medium">{attachment.name}</span>
              <span className="text-xs text-muted-foreground">Carregando preview...</span>
            </div>
          </div>
        );
      }

      if (imageUrl) {
        return (
          <div className="flex items-center gap-3 p-2 border rounded-lg">
            <img 
              src={imageUrl} 
              alt={attachment.name}
              className="w-20 h-20 object-cover rounded border cursor-pointer hover:opacity-80 transition-opacity"
              onClick={handleImageClick}
              title="Clique para visualizar"
            />
            <div className="flex flex-col gap-2">
              <span className="text-sm font-medium truncate">{attachment.name}</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onDownload(attachment)}
                className="w-fit"
              >
                <Download className="h-3 w-3 mr-1" />
                Baixar
              </Button>
            </div>
          </div>
        );
      }
      
      // Fallback for images that failed to load
      return (
        <div className="flex items-center gap-2 p-2 border rounded-lg">
          <div className="w-20 h-20 bg-muted rounded border flex items-center justify-center">
            <FileText className="w-6 h-6 text-muted-foreground" />
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-sm font-medium truncate">{attachment.name}</span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onDownload(attachment)}
              className="w-fit"
            >
              <Download className="h-3 w-3 mr-1" />
              Baixar
            </Button>
          </div>
        </div>
      );
    }

    return (
      <div className="flex items-center gap-2 p-2 border rounded-lg">
        <FileText className="w-4 h-4 text-muted-foreground" />
        <span className="text-sm truncate max-w-32">{attachment.name}</span>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => onDownload(attachment)}
        >
          <Download className="w-3 h-3" />
        </Button>
      </div>
    );
  };

  if (isLoading) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (!ticket) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden">
          <div className="flex items-center justify-center h-64">
            <p className="text-muted-foreground">Chamado não encontrado</p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle>Chamado #{ticket.ticket_number ? String(ticket.ticket_number).padStart(6, '0') : '—'}</DialogTitle>
        </DialogHeader>
        
        <div className="grid grid-cols-12 gap-6 h-[calc(90vh-8rem)]">
          {/* Left Column - Ticket Info */}
          <div className="col-span-3">
            <ScrollArea className="h-full">
              <div className="space-y-4">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm">Informações</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div>
                      <p className="text-sm font-medium">Status</p>
                      <Badge variant="default">{getStatusLabel(ticket.status)}</Badge>
                    </div>
                    
                    <div>
                      <p className="text-sm font-medium">Prioridade</p>
                      <Badge variant={getPriorityVariant(ticket.priority) as any}>
                        {getPriorityLabel(ticket.priority)}
                      </Badge>
                    </div>

                    <div>
                      <p className="text-sm font-medium">Categoria</p>
                      <p className="text-sm text-muted-foreground">{ticket.category?.name || 'Sem categoria'}</p>
                    </div>

                    {ticket.system?.name && (
                      <div>
                        <p className="text-sm font-medium">Sistema</p>
                        <p className="text-sm text-muted-foreground">{ticket.system.name}</p>
                      </div>
                    )}

                    <div>
                      <p className="text-sm font-medium">Criado em</p>
                      <p className="text-sm text-muted-foreground">
                        {format(new Date(ticket.created_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                      </p>
                    </div>
                  </CardContent>
                </Card>

                {ticket.attachments && ticket.attachments.length > 0 && (
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm">Anexos</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 gap-2">
                        {ticket.attachments.map((attachment: any, index: number) => (
                          <AttachmentPreview 
                            key={index} 
                            attachment={attachment} 
                            onDownload={downloadAttachment}
                          />
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            </ScrollArea>
          </div>

          {/* Center Column - Messages */}
          <div className="col-span-9 flex flex-col">
            <div className="mb-4">
              <h3 className="text-lg font-semibold">{ticket.title}</h3>
              <p className="text-muted-foreground">{ticket.description}</p>
            </div>

            <Separator className="mb-4" />

            <ScrollArea className="flex-1 mb-4">
              <div className="space-y-4">
                {ticket.messages?.map((message: any) => (
                  <div key={message.id} className="flex gap-3">
                    <Avatar className="w-8 h-8">
                      <AvatarFallback className="text-xs">
                        {getInitials(message.sender_name || 'U')}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-sm">{message.sender_name}</span>
                        <span className="text-xs text-muted-foreground">
                          {format(new Date(message.created_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                        </span>
                        {message.is_internal && (
                          <Badge variant="secondary" className="text-xs">Interno</Badge>
                        )}
                      </div>
                      <div className="text-sm bg-muted p-3 rounded-lg">
                        {message.message}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>

            {/* Message Input */}
            <div className="space-y-3">
              <Textarea
                placeholder="Digite sua mensagem..."
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                className="min-h-[80px]"
              />
              <div className="flex justify-end">
                <Button
                  onClick={handleSendMessage}
                  disabled={!newMessage.trim() || isSubmitting}
                >
                  {isSubmitting ? "Enviando..." : "Enviar Mensagem"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
      
      <ImageViewerModal
        images={imageAttachments}
        initialIndex={selectedImageIndex}
        open={imageViewerOpen}
        onOpenChange={setImageViewerOpen}
        onDownload={(attachment) => downloadAttachment({ 
          id: attachment.id, 
          name: attachment.name, 
          path: attachment.signedUrl.split('/').pop() || attachment.name 
        })}
      />
    </Dialog>
  );
};